import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { fetchSiteContent } from "@/lib/lumenApi";

const cycleOrder = [
  "effet-miroir",
  "discipline-assentiment",
  "ce-qui-depend-de-toi",
  "le-masque-et-la-structure",
  "terrain-avant-affrontement",
  "ce-que-tu-repetes-te-construit",
];

const cycleAtlas = {
  "effet-miroir": {
    phase: "Révélation",
    verb: "Voir",
    source: "Lecture du miroir intérieur",
    transformation: "Le réel cesse d’être décor et devient révélateur de ta propre matière.",
    prepares: "Prépare la chambre du discernement.",
    accent: "halo",
  },
  "discipline-assentiment": {
    phase: "Discernement",
    verb: "Filtrer",
    source: "Épictète • discipline des impressions",
    transformation: "Tu cesses de confondre apparition, interprétation et vérité.",
    prepares: "Prépare la juste répartition de la maîtrise.",
    accent: "mist",
  },
  "ce-qui-depend-de-toi": {
    phase: "Souveraineté",
    verb: "Se recentrer",
    source: "Épictète • Manuel §1",
    transformation: "Tu retrouves la part exacte de ton gouvernement intérieur sans illusion de contrôle total.",
    prepares: "Prépare la lecture adulte des scènes humaines.",
    accent: "ember",
  },
  "le-masque-et-la-structure": {
    phase: "Lecture structurelle",
    verb: "Lire",
    source: "La Rochefoucauld • Gracián • Machiavel",
    transformation: "Tu lis les scènes humaines au-delà de la façade déclarée, sans cynisme ni naïveté.",
    prepares: "Prépare la lecture stratégique des configurations.",
    accent: "violet",
  },
  "terrain-avant-affrontement": {
    phase: "Position juste",
    verb: "Entrer",
    source: "Sun Tzu • science de la configuration",
    transformation: "Tu cesses d’entrer par impulsion et apprends à lire terrain, moment, position et coût.",
    prepares: "Prépare l’incarnation durable de la lucidité.",
    accent: "halo",
  },
  "ce-que-tu-repetes-te-construit": {
    phase: "Caractère",
    verb: "Devenir",
    source: "Aristote • Éthique à Nicomaque",
    transformation: "La vérité cesse d’être seulement comprise et commence à prendre la forme d’une vie répétée.",
    prepares: "Scelle le premier cycle dans une forme de caractère.",
    accent: "mist",
  },
};

const cycleLaws = [
  {
    title: "Le cycle n’ajoute pas des thèmes.",
    copy: "Chaque chambre relève un plan du réel et oblige la suivante à descendre plus loin dans la manière de voir, de juger, d’agir et de devenir.",
  },
  {
    title: "Le parcours forme une montée continue.",
    copy: "Révélation, discernement, souveraineté, lecture structurelle, position juste et caractère ne sont pas des pages voisines : ce sont des seuils d’un même sanctuaire.",
  },
  {
    title: "La dernière chambre ne conclut pas : elle incarne.",
    copy: "Le premier cycle ne s’achève pas dans une idée supérieure, mais dans une vérité assez répétée pour devenir forme stable de vie.",
  },
];

function LoadingState() {
  return (
    <div className="lumen-page">
      <div className="lumen-shell flex min-h-screen items-center justify-center px-6 py-24">
        <div className="lumen-frame max-w-xl p-10 text-center">
          <p className="lumen-kicker" data-testid="journey-loading-kicker">
            LUMEN
          </p>
          <h1 className="lumen-title mt-4 text-4xl text-[var(--lumen-ivory)]" data-testid="journey-loading-title">
            La carte du cycle apparaît lentement.
          </h1>
          <p className="lumen-copy mt-4" data-testid="journey-loading-copy">
            Quelques instants suffisent pour laisser apparaître la lecture supérieure du parcours.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function JourneyPage() {
  const { user } = useAuth();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let active = true;

    fetchSiteContent()
      .then((data) => {
        if (active) {
          setSite(data);
        }
      })
      .catch(() => {
        if (active) {
          setErrorMessage("La carte du cycle reste silencieuse pour l’instant. Réessaie dans un moment.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [user]);

  const visibleLessons = useMemo(() => {
    if (!site) {
      return [];
    }

    return cycleOrder
      .map((slug) => site.lessons.find((lesson) => lesson.slug === slug))
      .filter(Boolean)
      .map((lesson) => ({ ...lesson, ...cycleAtlas[lesson.slug] }));
  }, [site]);

  if (loading || !site) {
    if (!loading && errorMessage) {
      return (
        <div className="lumen-page">
          <div className="lumen-shell flex min-h-screen items-center justify-center px-6 py-24">
            <div className="lumen-frame max-w-xl p-10 text-center">
              <p className="lumen-kicker" data-testid="journey-error-kicker">
                LUMEN
              </p>
              <h1 className="lumen-title mt-4 text-4xl text-[var(--lumen-ivory)]" data-testid="journey-error-title">
                La carte du sanctuaire n’est pas encore visible.
              </h1>
              <p className="lumen-copy mt-4" data-testid="journey-error-copy">
                {errorMessage}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return <LoadingState />;
  }

  return (
    <div className="lumen-page journey-page" id="top">
      <div className="lumen-shell pb-20">
        <header className="lumen-header fade-rise" data-testid="journey-header">
          <Link className="lumen-brand" to="/" data-testid="journey-brand-link">
            <BrandMark wrapperClassName="lumen-sigil" testId="journey-brand-sigil" imageTestId="journey-brand-sigil-image" />
            <span>{site.brand_name}</span>
          </Link>
          <div className="flex flex-wrap items-center gap-3" data-testid="journey-header-actions">
            <Link className="lumen-nav-link" to="/" data-testid="journey-home-link">
              Sanctuaire
            </Link>
            <a className="lumen-nav-link" href="#atlas" data-testid="journey-atlas-link">
              Atlas du cycle
            </a>
          </div>
        </header>

        <main className="journey-page-main space-y-0" data-testid="journey-main">
          <section className="lumen-scroll-stage stage-manifest journey-hero-stage grid gap-6 xl:grid-cols-[1.08fr_0.92fr]" data-testid="journey-hero-section">
            <div className="space-y-6 fade-rise delay-1">
              <p className="lumen-kicker" data-testid="journey-hero-kicker">
                Carte du premier cycle
              </p>
              <h1 className="lumen-title max-w-4xl text-5xl sm:text-6xl lg:text-7xl" data-testid="journey-hero-title">
                Le parcours initiatique devient enfin lisible comme une seule traversée.
              </h1>
              <p className="lumen-copy max-w-3xl text-base md:text-lg" data-testid="journey-hero-copy">
                Le premier cycle ne juxtapose pas six chambres. Il compose une montée : voir, filtrer, se recentrer, lire, entrer juste, devenir. Cette page en offre la carte noble, la logique et l’ordre vivant.
              </p>
              <div className="flex flex-wrap gap-4" data-testid="journey-hero-actions">
                <Button asChild className="lumen-gold-button" data-testid="journey-start-cycle-button">
                  <Link to="/lecons/effet-miroir">
                    Entrer par la première chambre
                    <ArrowRight />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="lumen-outline-button" data-testid="journey-return-home-button">
                  <Link to="/">
                    Revenir au sanctuaire
                    <ArrowLeft />
                  </Link>
                </Button>
              </div>
            </div>

            <Card className="lumen-card value-major journey-overview-card fade-rise delay-2" data-testid="journey-overview-card">
              <CardHeader className="space-y-4">
                <div className="lumen-panel-topline" data-testid="journey-overview-topline">
                  <p className="lumen-panel-kicker" data-testid="journey-overview-panel-kicker">
                    Architecture du cycle
                  </p>
                  <span className="lumen-plus">+</span>
                </div>
                <p className="lumen-kicker" data-testid="journey-overview-kicker">
                  Premier noyau vivant
                </p>
                <CardTitle className="lumen-card-title text-3xl" data-testid="journey-overview-title">
                  Une montée continue du regard jusqu’au caractère.
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="journey-metric-grid" data-testid="journey-metric-grid">
                  <div className="lumen-metric value-fragment" data-testid="journey-cycle-count-card">
                    <p className="lumen-metric-value" data-testid="journey-cycle-count-value">
                      {visibleLessons.length}
                    </p>
                    <p className="lumen-metric-copy" data-testid="journey-cycle-count-copy">
                      chambres dans le premier cycle
                    </p>
                  </div>
                  <div className="lumen-metric value-fragment" data-testid="journey-territory-count-card">
                    <p className="lumen-metric-value" data-testid="journey-territory-count-value">
                      6
                    </p>
                    <p className="lumen-metric-copy" data-testid="journey-territory-count-copy">
                      seuils pour passer de la lucidité à la forme de vie
                    </p>
                  </div>
                </div>
                <p className="journey-overview-note lumen-copy" data-testid="journey-overview-note">
                  Cette carte n’est pas un sommaire fonctionnel. Elle montre comment chaque chambre porte la suivante et comment le cycle entier transforme une compréhension en manière de vivre.
                </p>
              </CardContent>
            </Card>
          </section>

          <section className="lumen-scroll-stage stage-doctrine journey-axis-stage space-y-8" data-testid="journey-axis-section">
            <div className="space-y-3 fade-rise delay-2">
              <p className="lumen-kicker" data-testid="journey-axis-kicker">
                Ligne du cycle
              </p>
              <div className="lumen-section-heading" data-testid="journey-axis-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="journey-axis-title">
                  Une seule montée : de la première révélation jusqu’à la forme acquise du caractère.
                </h2>
                <span className="lumen-plus" data-testid="journey-axis-plus">
                  +
                </span>
              </div>
              <Separator className="lumen-separator max-w-3xl" data-testid="journey-axis-separator" />
            </div>

            <div className="journey-phase-grid" data-testid="journey-phase-grid">
              {visibleLessons.map((lesson, index) => (
                <Card className={`lumen-card value-doctrine accent-${lesson.accent} journey-phase-card`} key={lesson.slug} data-testid={`journey-phase-card-${lesson.slug}`}>
                  <CardHeader className="space-y-4">
                    <div className="lumen-panel-topline" data-testid={`journey-phase-topline-${lesson.slug}`}>
                      <p className="lumen-panel-kicker" data-testid={`journey-phase-panel-kicker-${lesson.slug}`}>
                        Phase {String(index + 1).padStart(2, "0")}
                      </p>
                      <span className="lumen-plus">+</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="lumen-chip" data-testid={`journey-phase-verb-${lesson.slug}`}>
                        {lesson.verb}
                      </span>
                      <span className="lumen-status-pill open" data-testid={`journey-phase-status-${lesson.slug}`}>
                        {lesson.phase}
                      </span>
                    </div>
                    <CardTitle className="lumen-card-title" data-testid={`journey-phase-title-${lesson.slug}`}>
                      {lesson.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="lumen-copy" data-testid={`journey-phase-copy-${lesson.slug}`}>
                      {lesson.transformation}
                    </p>
                    <p className="lumen-footnote" data-testid={`journey-phase-footnote-${lesson.slug}`}>
                      {lesson.prepares}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section id="atlas" className="lumen-scroll-stage stage-threshold journey-atlas-stage space-y-8" data-testid="journey-atlas-section">
            <div className="space-y-3 fade-rise delay-2">
              <p className="lumen-kicker" data-testid="journey-atlas-kicker">
                Atlas du parcours
              </p>
              <div className="lumen-section-heading" data-testid="journey-atlas-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="journey-atlas-title">
                  Chaque chambre porte une source réelle, une fonction propre et une transformation précise du regard.
                </h2>
                <span className="lumen-plus" data-testid="journey-atlas-plus">
                  +
                </span>
              </div>
            </div>

            <div className="grid gap-5 xl:grid-cols-2 lumen-subgrid cluster-doctrine" data-testid="journey-atlas-grid">
              {visibleLessons.map((lesson) => (
                <Card className={`lumen-card value-access accent-${lesson.accent} journey-atlas-card`} key={lesson.slug} data-testid={`journey-atlas-card-${lesson.slug}`}>
                  <CardHeader className="space-y-4">
                    <div className="lumen-panel-topline" data-testid={`journey-atlas-topline-${lesson.slug}`}>
                      <p className="lumen-panel-kicker" data-testid={`journey-atlas-panel-kicker-${lesson.slug}`}>
                        {lesson.step_label}
                      </p>
                      <span className="lumen-plus">+</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="lumen-chip" data-testid={`journey-atlas-phase-${lesson.slug}`}>
                        {lesson.phase}
                      </span>
                      <span className={`lumen-status-pill ${lesson.state}`} data-testid={`journey-atlas-state-${lesson.slug}`}>
                        {lesson.state === "open" ? "Chambre ouverte" : "Aperçu"}
                      </span>
                    </div>
                    <CardTitle className="lumen-card-title text-3xl" data-testid={`journey-atlas-title-${lesson.slug}`}>
                      {lesson.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <p className="lumen-copy" data-testid={`journey-atlas-transformation-${lesson.slug}`}>
                      {lesson.transformation}
                    </p>
                    <div className="journey-atlas-meta" data-testid={`journey-atlas-meta-${lesson.slug}`}>
                      <div className="journey-atlas-meta-item" data-testid={`journey-atlas-source-${lesson.slug}`}>
                        <BookOpen className="h-4 w-4 text-[var(--lumen-gold)]" />
                        <span>{lesson.source}</span>
                      </div>
                      <div className="journey-atlas-meta-item" data-testid={`journey-atlas-prepares-${lesson.slug}`}>
                        <Compass className="h-4 w-4 text-[var(--lumen-gold)]" />
                        <span>{lesson.prepares}</span>
                      </div>
                    </div>
                    <Button asChild className="lumen-gold-button threshold-action-button threshold-traversal-button w-full" data-testid={`journey-open-lesson-button-${lesson.slug}`}>
                      <Link to={`/lecons/${lesson.slug}`}>
                        {lesson.state === "open" ? "Ouvrir la chambre" : "Voir l’aperçu"}
                        <ArrowRight />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="lumen-scroll-stage stage-fragment journey-laws-stage space-y-8" data-testid="journey-laws-section">
            <div className="space-y-3 fade-rise delay-3">
              <p className="lumen-kicker" data-testid="journey-laws-kicker">
                Loi du parcours
              </p>
              <div className="lumen-section-heading" data-testid="journey-laws-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="journey-laws-title">
                  Le premier cycle n’est pas une collection de chambres ; il est une forme de lecture qui se referme dans le caractère.
                </h2>
                <span className="lumen-plus" data-testid="journey-laws-plus">
                  +
                </span>
              </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-3 lumen-subgrid cluster-fragment" data-testid="journey-laws-grid">
              {cycleLaws.map((law, index) => (
                <Card className="lumen-card value-fragment journey-law-card" key={law.title} data-testid={`journey-law-card-${index + 1}`}>
                  <CardHeader className="space-y-4">
                    <div className="lumen-panel-topline" data-testid={`journey-law-topline-${index + 1}`}>
                      <p className="lumen-panel-kicker" data-testid={`journey-law-panel-kicker-${index + 1}`}>
                        Loi {String(index + 1).padStart(2, "0")}
                      </p>
                      <span className="lumen-plus">+</span>
                    </div>
                    <CardTitle className="lumen-card-title" data-testid={`journey-law-title-${index + 1}`}>
                      {law.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="lumen-copy" data-testid={`journey-law-copy-${index + 1}`}>
                      {law.copy}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="lumen-scroll-stage stage-threshold stage-access stage-pivot lumen-frame value-access journey-closing-shell space-y-5 p-8 fade-rise delay-3" data-testid="journey-closing-section">
            <p className="lumen-kicker" data-testid="journey-closing-kicker">
              Porte d’entrée noble
            </p>
            <p className="journey-closing-label lumen-copy" data-testid="journey-closing-label">
              Seuil du cycle
            </p>
            <p className="lumen-title text-3xl leading-tight sm:text-4xl" data-testid="journey-closing-title">
              Le premier cycle peut être ouvert d’un seul passage.
            </p>
            <p className="journey-closing-support lumen-copy" data-testid="journey-closing-support">
              29 € • Cycle I complet.
            </p>
            <div className="flex flex-wrap gap-4" data-testid="journey-closing-actions">
              <Button asChild className="lumen-gold-button threshold-action-button threshold-traversal-button" data-testid="journey-closing-first-button">
                <Link to="/entrer">
                  Entrer dans LUMEN — 29 €
                  <ArrowRight />
                </Link>
              </Button>
              <Button asChild className="lumen-outline-button threshold-action-button threshold-reorient-button" data-testid="journey-closing-integral-button" variant="outline">
                <Link to="/entrer">
                  LUMEN Intégral — 79 €
                  <ArrowRight />
                </Link>
              </Button>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}